// LanguageContext.jsx — Global i18n for OkBot platform (comprehensive)
import React, { createContext, useContext, useState, useEffect } from 'react';

export const LANGUAGES = [
  { code: 'es', flag: '🇪🇸', label: 'Español',    nativeName: 'Español' },
  { code: 'en', flag: '🇺🇸', label: 'English',    nativeName: 'English' },
  { code: 'pt', flag: '🇧🇷', label: 'Português',  nativeName: 'Português' },
  { code: 'fr', flag: '🇫🇷', label: 'Français',   nativeName: 'Français' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch',    nativeName: 'Deutsch' },
  { code: 'it', flag: '🇮🇹', label: 'Italiano',   nativeName: 'Italiano' },
  { code: 'ja', flag: '🇯🇵', label: 'Japonés',    nativeName: '日本語' },
  { code: 'zh', flag: '🇨🇳', label: 'Chino',      nativeName: '中文' },
  { code: 'ko', flag: '🇰🇷', label: 'Coreano',    nativeName: '한국어' },
  { code: 'ar', flag: '🇸🇦', label: 'Árabe',      nativeName: 'العربية' },
  { code: 'ru', flag: '🇷🇺', label: 'Ruso',       nativeName: 'Русский' },
  { code: 'nl', flag: '🇳🇱', label: 'Neerlandés', nativeName: 'Nederlands' },
  { code: 'tr', flag: '🇹🇷', label: 'Turco',      nativeName: 'Türkçe' },
  { code: 'pl', flag: '🇵🇱', label: 'Polaco',     nativeName: 'Polski' },
  { code: 'id', flag: '🇮🇩', label: 'Indonesio',  nativeName: 'Indonesia' },
];

// ── Full translation table — 100+ keys, 15 languages ─────────────────────────
const T = {
  // ════════════════════════════════════════════════════════════════════════════
  es: {
    // Sidebar
    chat: 'Chat', intelligence: 'OkBot AI+', marketplace: 'Marketplace',
    calendar: 'Calendario', notes: 'Notas', reminders: 'Recordatorios',
    lists: 'Listas', finance: 'Finanzas', health: 'Salud', travel: 'Viajes',
    music: 'Música', documents: 'Documentos IA', memes: 'Memes',
    movies: 'Pelis & Series', support: 'Soporte', integrations: 'Integraciones',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Configuración', logout: 'Salir',
    // Chat
    chatPlaceholder: 'Escribe tu mensaje… (Enter para enviar, Shift+Enter nueva línea)',
    listen: 'Escuchar', stop: 'Detener', voice: 'Voz',
    newChat: 'Nueva conversación', history: 'Historial', share: 'Compartir',
    sendError: 'Error al enviar mensaje', recording: 'Grabando… toca otra vez para enviar',
    transcribing: 'Transcribiendo audio…', transcribeError: 'Error transcribiendo',
    audioTooShort: 'Audio muy corto', noAudio: 'No se entendió el audio',
    moreDetail: 'Dame una respuesta más detallada', thinking: 'Pensando…',
    // Settings
    settingsTitle: 'Configuración', profile: 'Perfil', plans: 'Planes OkBot',
    appearance: 'Apariencia', notifications: 'Notificaciones',
    integrationsLabel: 'Integraciones', security: 'Seguridad', language: 'Idioma',
    saveChanges: 'Guardar cambios', saving: 'Guardando…',
    languageDesc: 'Idioma de la plataforma y respuestas del chat',
    languageChanged: 'Idioma cambiado a',
    theme: 'Tema', darkMode: 'Modo oscuro', lightMode: 'Modo claro',
    yourProfile: 'Tu Perfil', changePhoto: 'Cambiar foto',
    // Common actions
    search: 'Buscar', cancel: 'Cancelar', close: 'Cerrar', delete: 'Eliminar',
    connect: 'Conectar', update: 'Actualizar', back: 'Volver', accept: 'Aceptar',
    comingSoon: 'Próximamente', loading: 'Cargando…',
    viewMore: 'Ver más', viewLess: 'Ver menos', play: 'Reproducir', pause: 'Pausar',
    download: 'Descargar', generate: 'Generar', clear: 'Limpiar', retry: 'Reintentar',
    noData: 'Sin datos', errorOccurred: 'Ocurrió un error', save: 'Guardar',
    // OkBook
    searchBooks: 'Buscar por título, autor o idioma…',
    okbookSource: 'LibriVox · 20k+ títulos',
    continueListening: 'Seguir escuchando',
    exploreGenres: 'Explorar géneros',
    newArrivals: 'Novedades en LibriVox',
    myFavorites: 'Mis favoritos',
    narrateWithAI: 'Narrar con IA',
    narrateHint: 'Pega tu texto, capítulo o fragmento de libro…',
    generateNarration: 'Narrar texto',
    generatingAudio: 'Generando audio…',
    noFavorites: 'Aún no tienes favoritos',
    noFavoritesHint: 'Toca ❤️ en cualquier audiolibro para guardarlo',
    noResults: 'No se encontraron audiolibros',
    noResultsHint: 'Intenta con otro término o género',
    chapters: 'Capítulos', narrator: 'Narrador',
    addToFavorites: 'Añadir a favoritos', removeFromFavorites: 'En favoritos',
    playbackSpeed: 'Velocidad', rewind30: 'Retroceder 30s', forward30: 'Adelantar 30s',
    nowPlaying: 'Reproduciendo…', newSearch: 'Nueva búsqueda…',
    searchingBooks: 'Buscando audiolibros…', readyAudio: 'Audio listo',
    downloadMp3: 'Descargar MP3', charsMax: 'caracteres',
    resultsFor: 'Resultados para', noChapters: 'No hay capítulos disponibles',
    // Genres
    genre_adventure: 'Aventura', genre_classics: 'Clásicos', genre_mystery: 'Misterio',
    genre_science: 'Ciencia', genre_history: 'Historia', genre_fantasy: 'Fantasía',
    genre_romance: 'Romance', genre_horror: 'Terror', genre_philosophy: 'Filosofía',
    genre_biography: 'Biografías', genre_children: 'Infantil', genre_poetry: 'Poesía',
  },
  // ════════════════════════════════════════════════════════════════════════════
  en: {
    chat: 'Chat', intelligence: 'OkBot AI+', marketplace: 'Marketplace',
    calendar: 'Calendar', notes: 'Notes', reminders: 'Reminders',
    lists: 'Lists', finance: 'Finance', health: 'Health', travel: 'Travel',
    music: 'Music', documents: 'AI Documents', memes: 'Memes',
    movies: 'Movies & Series', support: 'Support', integrations: 'Integrations',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Settings', logout: 'Log out',
    chatPlaceholder: 'Type your message… (Enter to send, Shift+Enter new line)',
    listen: 'Listen', stop: 'Stop', voice: 'Voice',
    newChat: 'New conversation', history: 'History', share: 'Share',
    sendError: 'Error sending message', recording: 'Recording… tap again to send',
    transcribing: 'Transcribing audio…', transcribeError: 'Transcription error',
    audioTooShort: 'Audio too short', noAudio: 'Audio not understood',
    moreDetail: 'Give me a more detailed answer', thinking: 'Thinking…',
    settingsTitle: 'Settings', profile: 'Profile', plans: 'OkBot Plans',
    appearance: 'Appearance', notifications: 'Notifications',
    integrationsLabel: 'Integrations', security: 'Security', language: 'Language',
    saveChanges: 'Save changes', saving: 'Saving…',
    languageDesc: 'Platform language and chat response language',
    languageChanged: 'Language changed to',
    theme: 'Theme', darkMode: 'Dark mode', lightMode: 'Light mode',
    yourProfile: 'Your Profile', changePhoto: 'Change photo',
    search: 'Search', cancel: 'Cancel', close: 'Close', delete: 'Delete',
    connect: 'Connect', update: 'Update', back: 'Back', accept: 'Accept',
    comingSoon: 'Coming soon', loading: 'Loading…',
    viewMore: 'View more', viewLess: 'View less', play: 'Play', pause: 'Pause',
    download: 'Download', generate: 'Generate', clear: 'Clear', retry: 'Retry',
    noData: 'No data', errorOccurred: 'An error occurred', save: 'Save',
    searchBooks: 'Search by title, author or language…',
    okbookSource: 'LibriVox · 20k+ titles',
    continueListening: 'Continue listening',
    exploreGenres: 'Explore genres',
    newArrivals: 'New in LibriVox',
    myFavorites: 'My favorites',
    narrateWithAI: 'Narrate with AI',
    narrateHint: 'Paste your text, chapter or book excerpt…',
    generateNarration: 'Narrate text',
    generatingAudio: 'Generating audio…',
    noFavorites: 'No favorites yet',
    noFavoritesHint: 'Tap ❤️ on any audiobook to save it here',
    noResults: 'No audiobooks found',
    noResultsHint: 'Try a different term or genre',
    chapters: 'Chapters', narrator: 'Narrator',
    addToFavorites: 'Add to favorites', removeFromFavorites: 'In favorites',
    playbackSpeed: 'Speed', rewind30: 'Rewind 30s', forward30: 'Forward 30s',
    nowPlaying: 'Now playing…', newSearch: 'New search…',
    searchingBooks: 'Searching audiobooks…', readyAudio: 'Audio ready',
    downloadMp3: 'Download MP3', charsMax: 'characters',
    resultsFor: 'Results for', noChapters: 'No chapters available',
    genre_adventure: 'Adventure', genre_classics: 'Classics', genre_mystery: 'Mystery',
    genre_science: 'Science', genre_history: 'History', genre_fantasy: 'Fantasy',
    genre_romance: 'Romance', genre_horror: 'Horror', genre_philosophy: 'Philosophy',
    genre_biography: 'Biographies', genre_children: 'Children', genre_poetry: 'Poetry',
  },
  // ════════════════════════════════════════════════════════════════════════════
  pt: {
    chat: 'Chat', intelligence: 'OkBot AI+', marketplace: 'Marketplace',
    calendar: 'Calendário', notes: 'Notas', reminders: 'Lembretes',
    lists: 'Listas', finance: 'Finanças', health: 'Saúde', travel: 'Viagens',
    music: 'Música', documents: 'Documentos IA', memes: 'Memes',
    movies: 'Filmes & Séries', support: 'Suporte', integrations: 'Integrações',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Configurações', logout: 'Sair',
    chatPlaceholder: 'Digite sua mensagem… (Enter para enviar, Shift+Enter nova linha)',
    listen: 'Ouvir', stop: 'Parar', voice: 'Voz',
    newChat: 'Nova conversa', history: 'Histórico', share: 'Compartilhar',
    sendError: 'Erro ao enviar mensagem', recording: 'Gravando… toque novamente para enviar',
    transcribing: 'Transcrevendo áudio…', transcribeError: 'Erro ao transcrever',
    audioTooShort: 'Áudio muito curto', noAudio: 'Áudio não compreendido',
    moreDetail: 'Me dê uma resposta mais detalhada', thinking: 'Pensando…',
    settingsTitle: 'Configurações', profile: 'Perfil', plans: 'Planos OkBot',
    appearance: 'Aparência', notifications: 'Notificações',
    integrationsLabel: 'Integrações', security: 'Segurança', language: 'Idioma',
    saveChanges: 'Salvar alterações', saving: 'Salvando…',
    languageDesc: 'Idioma da plataforma e respostas do chat',
    languageChanged: 'Idioma alterado para',
    theme: 'Tema', darkMode: 'Modo escuro', lightMode: 'Modo claro',
    yourProfile: 'Seu Perfil', changePhoto: 'Alterar foto',
    search: 'Buscar', cancel: 'Cancelar', close: 'Fechar', delete: 'Excluir',
    connect: 'Conectar', update: 'Atualizar', back: 'Voltar', accept: 'Aceitar',
    comingSoon: 'Em breve', loading: 'Carregando…',
    viewMore: 'Ver mais', viewLess: 'Ver menos', play: 'Reproduzir', pause: 'Pausar',
    download: 'Baixar', generate: 'Gerar', clear: 'Limpar', retry: 'Tentar novamente',
    noData: 'Sem dados', errorOccurred: 'Ocorreu um erro', save: 'Salvar',
    searchBooks: 'Buscar por título, autor ou idioma…',
    okbookSource: 'LibriVox · 20k+ títulos',
    continueListening: 'Continuar ouvindo',
    exploreGenres: 'Explorar gêneros',
    newArrivals: 'Novidades no LibriVox',
    myFavorites: 'Meus favoritos',
    narrateWithAI: 'Narrar com IA',
    narrateHint: 'Cole seu texto, capítulo ou trecho de livro…',
    generateNarration: 'Narrar texto',
    generatingAudio: 'Gerando áudio…',
    noFavorites: 'Ainda sem favoritos',
    noFavoritesHint: 'Toque ❤️ em qualquer audiolivro para salvar',
    noResults: 'Nenhum audiolivro encontrado',
    noResultsHint: 'Tente outro termo ou gênero',
    chapters: 'Capítulos', narrator: 'Narrador',
    addToFavorites: 'Adicionar aos favoritos', removeFromFavorites: 'Nos favoritos',
    playbackSpeed: 'Velocidade', rewind30: 'Voltar 30s', forward30: 'Avançar 30s',
    nowPlaying: 'Reproduzindo…', newSearch: 'Nova pesquisa…',
    searchingBooks: 'Buscando audiolivros…', readyAudio: 'Áudio pronto',
    downloadMp3: 'Baixar MP3', charsMax: 'caracteres',
    resultsFor: 'Resultados para', noChapters: 'Sem capítulos disponíveis',
    genre_adventure: 'Aventura', genre_classics: 'Clássicos', genre_mystery: 'Mistério',
    genre_science: 'Ciência', genre_history: 'História', genre_fantasy: 'Fantasia',
    genre_romance: 'Romance', genre_horror: 'Terror', genre_philosophy: 'Filosofia',
    genre_biography: 'Biografias', genre_children: 'Infantil', genre_poetry: 'Poesia',
  },
  // ════════════════════════════════════════════════════════════════════════════
  fr: {
    chat: 'Chat', intelligence: 'OkBot AI+', marketplace: 'Marketplace',
    calendar: 'Calendrier', notes: 'Notes', reminders: 'Rappels',
    lists: 'Listes', finance: 'Finances', health: 'Santé', travel: 'Voyages',
    music: 'Musique', documents: 'Documents IA', memes: 'Mèmes',
    movies: 'Films & Séries', support: 'Assistance', integrations: 'Intégrations',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Paramètres', logout: 'Déconnexion',
    chatPlaceholder: 'Tapez votre message… (Entrée pour envoyer, Maj+Entrée nouvelle ligne)',
    listen: 'Écouter', stop: 'Arrêter', voice: 'Voix',
    newChat: 'Nouvelle conversation', history: 'Historique', share: 'Partager',
    sendError: "Erreur d'envoi", recording: 'Enregistrement… tapez à nouveau pour envoyer',
    transcribing: 'Transcription audio…', transcribeError: 'Erreur de transcription',
    audioTooShort: 'Audio trop court', noAudio: 'Audio incompris',
    moreDetail: 'Donnez-moi une réponse plus détaillée', thinking: 'Réflexion…',
    settingsTitle: 'Paramètres', profile: 'Profil', plans: 'Formules OkBot',
    appearance: 'Apparence', notifications: 'Notifications',
    integrationsLabel: 'Intégrations', security: 'Sécurité', language: 'Langue',
    saveChanges: 'Enregistrer', saving: 'Enregistrement…',
    languageDesc: 'Langue de la plateforme et des réponses',
    languageChanged: 'Langue changée en',
    theme: 'Thème', darkMode: 'Mode sombre', lightMode: 'Mode clair',
    yourProfile: 'Votre Profil', changePhoto: 'Changer la photo',
    search: 'Rechercher', cancel: 'Annuler', close: 'Fermer', delete: 'Supprimer',
    connect: 'Connecter', update: 'Mettre à jour', back: 'Retour', accept: 'Accepter',
    comingSoon: 'Bientôt', loading: 'Chargement…',
    viewMore: 'Voir plus', viewLess: 'Voir moins', play: 'Lire', pause: 'Pause',
    download: 'Télécharger', generate: 'Générer', clear: 'Effacer', retry: 'Réessayer',
    noData: 'Pas de données', errorOccurred: 'Une erreur est survenue', save: 'Sauvegarder',
    searchBooks: 'Rechercher par titre, auteur ou langue…',
    okbookSource: 'LibriVox · 20k+ titres',
    continueListening: 'Continuer l\'écoute',
    exploreGenres: 'Explorer les genres',
    newArrivals: 'Nouveautés LibriVox',
    myFavorites: 'Mes favoris',
    narrateWithAI: 'Narrer avec l\'IA',
    narrateHint: 'Collez votre texte, chapitre ou extrait de livre…',
    generateNarration: 'Narrer le texte',
    generatingAudio: 'Génération de l\'audio…',
    noFavorites: 'Pas encore de favoris',
    noFavoritesHint: 'Appuyez sur ❤️ sur un livre audio pour le sauvegarder',
    noResults: 'Aucun livre audio trouvé',
    noResultsHint: 'Essayez un autre terme ou genre',
    chapters: 'Chapitres', narrator: 'Narrateur',
    addToFavorites: 'Ajouter aux favoris', removeFromFavorites: 'Dans les favoris',
    playbackSpeed: 'Vitesse', rewind30: 'Reculer 30s', forward30: 'Avancer 30s',
    nowPlaying: 'En cours…', newSearch: 'Nouvelle recherche…',
    searchingBooks: 'Recherche de livres audio…', readyAudio: 'Audio prêt',
    downloadMp3: 'Télécharger MP3', charsMax: 'caractères',
    resultsFor: 'Résultats pour', noChapters: 'Aucun chapitre disponible',
    genre_adventure: 'Aventure', genre_classics: 'Classiques', genre_mystery: 'Mystère',
    genre_science: 'Science', genre_history: 'Histoire', genre_fantasy: 'Fantaisie',
    genre_romance: 'Romance', genre_horror: 'Horreur', genre_philosophy: 'Philosophie',
    genre_biography: 'Biographies', genre_children: 'Enfants', genre_poetry: 'Poésie',
  },
  // ════════════════════════════════════════════════════════════════════════════
  de: {
    chat: 'Chat', intelligence: 'OkBot AI+', marketplace: 'Marktplatz',
    calendar: 'Kalender', notes: 'Notizen', reminders: 'Erinnerungen',
    lists: 'Listen', finance: 'Finanzen', health: 'Gesundheit', travel: 'Reisen',
    music: 'Musik', documents: 'KI-Dokumente', memes: 'Memes',
    movies: 'Filme & Serien', support: 'Support', integrations: 'Integrationen',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Einstellungen', logout: 'Abmelden',
    chatPlaceholder: 'Nachricht eingeben… (Enter senden, Shift+Enter neue Zeile)',
    listen: 'Anhören', stop: 'Stoppen', voice: 'Stimme',
    newChat: 'Neues Gespräch', history: 'Verlauf', share: 'Teilen',
    sendError: 'Fehler beim Senden', recording: 'Aufnahme… erneut tippen zum Senden',
    transcribing: 'Transkription…', transcribeError: 'Transkriptionsfehler',
    audioTooShort: 'Audio zu kurz', noAudio: 'Audio nicht verstanden',
    moreDetail: 'Gib mir eine ausführlichere Antwort', thinking: 'Denken…',
    settingsTitle: 'Einstellungen', profile: 'Profil', plans: 'OkBot-Pläne',
    appearance: 'Aussehen', notifications: 'Benachrichtigungen',
    integrationsLabel: 'Integrationen', security: 'Sicherheit', language: 'Sprache',
    saveChanges: 'Änderungen speichern', saving: 'Wird gespeichert…',
    languageDesc: 'Plattformsprache und Chat-Antwortsprache',
    languageChanged: 'Sprache geändert zu',
    theme: 'Design', darkMode: 'Dunkelmodus', lightMode: 'Hellmodus',
    yourProfile: 'Mein Profil', changePhoto: 'Foto ändern',
    search: 'Suchen', cancel: 'Abbrechen', close: 'Schließen', delete: 'Löschen',
    connect: 'Verbinden', update: 'Aktualisieren', back: 'Zurück', accept: 'Akzeptieren',
    comingSoon: 'Demnächst', loading: 'Wird geladen…',
    viewMore: 'Mehr anzeigen', viewLess: 'Weniger anzeigen', play: 'Abspielen', pause: 'Pause',
    download: 'Herunterladen', generate: 'Generieren', clear: 'Löschen', retry: 'Wiederholen',
    noData: 'Keine Daten', errorOccurred: 'Ein Fehler ist aufgetreten', save: 'Speichern',
    searchBooks: 'Nach Titel, Autor oder Sprache suchen…',
    okbookSource: 'LibriVox · 20k+ Titel',
    continueListening: 'Weiterhören',
    exploreGenres: 'Genres erkunden',
    newArrivals: 'Neu bei LibriVox',
    myFavorites: 'Meine Favoriten',
    narrateWithAI: 'Mit KI erzählen',
    narrateHint: 'Text, Kapitel oder Buchausschnitt einfügen…',
    generateNarration: 'Text vorlesen',
    generatingAudio: 'Audio wird generiert…',
    noFavorites: 'Noch keine Favoriten',
    noFavoritesHint: 'Tippe ❤️ auf ein Hörbuch, um es zu speichern',
    noResults: 'Keine Hörbücher gefunden',
    noResultsHint: 'Versuche einen anderen Begriff oder Genre',
    chapters: 'Kapitel', narrator: 'Erzähler',
    addToFavorites: 'Zu Favoriten hinzufügen', removeFromFavorites: 'In Favoriten',
    playbackSpeed: 'Geschwindigkeit', rewind30: '30s zurück', forward30: '30s vor',
    nowPlaying: 'Wird abgespielt…', newSearch: 'Neue Suche…',
    searchingBooks: 'Hörbücher suchen…', readyAudio: 'Audio bereit',
    downloadMp3: 'MP3 herunterladen', charsMax: 'Zeichen',
    resultsFor: 'Ergebnisse für', noChapters: 'Keine Kapitel verfügbar',
    genre_adventure: 'Abenteuer', genre_classics: 'Klassiker', genre_mystery: 'Krimi',
    genre_science: 'Wissenschaft', genre_history: 'Geschichte', genre_fantasy: 'Fantasy',
    genre_romance: 'Romantik', genre_horror: 'Horror', genre_philosophy: 'Philosophie',
    genre_biography: 'Biografien', genre_children: 'Kinder', genre_poetry: 'Lyrik',
  },
  // ════════════════════════════════════════════════════════════════════════════
  it: {
    chat: 'Chat', intelligence: 'OkBot AI+', marketplace: 'Marketplace',
    calendar: 'Calendario', notes: 'Note', reminders: 'Promemoria',
    lists: 'Liste', finance: 'Finanze', health: 'Salute', travel: 'Viaggi',
    music: 'Musica', documents: 'Documenti IA', memes: 'Meme',
    movies: 'Film & Serie', support: 'Assistenza', integrations: 'Integrazioni',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Impostazioni', logout: 'Esci',
    chatPlaceholder: 'Scrivi il messaggio… (Invio invia, Maiusc+Invio nuova riga)',
    listen: 'Ascolta', stop: 'Ferma', voice: 'Voce',
    newChat: 'Nuova conversazione', history: 'Cronologia', share: 'Condividi',
    sendError: "Errore nell'invio", recording: 'Registrazione… tocca di nuovo per inviare',
    transcribing: 'Trascrizione audio…', transcribeError: 'Errore di trascrizione',
    audioTooShort: 'Audio troppo corto', noAudio: 'Audio non compreso',
    moreDetail: 'Dammi una risposta più dettagliata', thinking: 'Pensando…',
    settingsTitle: 'Impostazioni', profile: 'Profilo', plans: 'Piani OkBot',
    appearance: 'Aspetto', notifications: 'Notifiche',
    integrationsLabel: 'Integrazioni', security: 'Sicurezza', language: 'Lingua',
    saveChanges: 'Salva modifiche', saving: 'Salvataggio…',
    languageDesc: 'Lingua della piattaforma e delle risposte',
    languageChanged: 'Lingua cambiata in',
    theme: 'Tema', darkMode: 'Modalità scura', lightMode: 'Modalità chiara',
    yourProfile: 'Il tuo Profilo', changePhoto: 'Cambia foto',
    search: 'Cerca', cancel: 'Annulla', close: 'Chiudi', delete: 'Elimina',
    connect: 'Connetti', update: 'Aggiorna', back: 'Indietro', accept: 'Accetta',
    comingSoon: 'Prossimamente', loading: 'Caricamento…',
    viewMore: 'Vedi di più', viewLess: 'Vedi meno', play: 'Riproduci', pause: 'Pausa',
    download: 'Scarica', generate: 'Genera', clear: 'Cancella', retry: 'Riprova',
    noData: 'Nessun dato', errorOccurred: 'Si è verificato un errore', save: 'Salva',
    searchBooks: 'Cerca per titolo, autore o lingua…',
    okbookSource: 'LibriVox · 20k+ titoli',
    continueListening: 'Continua ad ascoltare',
    exploreGenres: 'Esplora generi',
    newArrivals: 'Novità su LibriVox',
    myFavorites: 'I miei preferiti',
    narrateWithAI: 'Narra con IA',
    narrateHint: 'Incolla il tuo testo, capitolo o estratto…',
    generateNarration: 'Narra il testo',
    generatingAudio: 'Generazione audio…',
    noFavorites: 'Ancora nessun preferito',
    noFavoritesHint: 'Tocca ❤️ su qualsiasi audiolibro per salvarlo',
    noResults: 'Nessun audiolibro trovato',
    noResultsHint: 'Prova un altro termine o genere',
    chapters: 'Capitoli', narrator: 'Narratore',
    addToFavorites: 'Aggiungi ai preferiti', removeFromFavorites: 'Nei preferiti',
    playbackSpeed: 'Velocità', rewind30: 'Indietro 30s', forward30: 'Avanti 30s',
    nowPlaying: 'In riproduzione…', newSearch: 'Nuova ricerca…',
    searchingBooks: 'Ricerca audiolibri…', readyAudio: 'Audio pronto',
    downloadMp3: 'Scarica MP3', charsMax: 'caratteri',
    resultsFor: 'Risultati per', noChapters: 'Nessun capitolo disponibile',
    genre_adventure: 'Avventura', genre_classics: 'Classici', genre_mystery: 'Mistero',
    genre_science: 'Scienza', genre_history: 'Storia', genre_fantasy: 'Fantasy',
    genre_romance: 'Romantico', genre_horror: 'Horror', genre_philosophy: 'Filosofia',
    genre_biography: 'Biografie', genre_children: 'Bambini', genre_poetry: 'Poesia',
  },
  // ════════════════════════════════════════════════════════════════════════════
  ja: {
    chat: 'チャット', intelligence: 'OkBot AI+', marketplace: 'マーケット',
    calendar: 'カレンダー', notes: 'メモ', reminders: 'リマインダー',
    lists: 'リスト', finance: '財務', health: '健康', travel: '旅行',
    music: '音楽', documents: 'AIドキュメント', memes: 'ミーム',
    movies: '映画・シリーズ', support: 'サポート', integrations: '連携',
    okhome: 'OkHome', okbook: 'OkBook', settings: '設定', logout: 'ログアウト',
    chatPlaceholder: 'メッセージを入力… (Enterで送信、Shift+Enterで改行)',
    listen: '聞く', stop: '停止', voice: '音声',
    newChat: '新しい会話', history: '履歴', share: '共有',
    sendError: '送信エラー', recording: '録音中… もう一度タップして送信',
    transcribing: '文字起こし中…', transcribeError: '文字起こしエラー',
    audioTooShort: '音声が短すぎます', noAudio: '音声を認識できませんでした',
    moreDetail: 'もっと詳しく教えてください', thinking: '考え中…',
    settingsTitle: '設定', profile: 'プロフィール', plans: 'OkBotプラン',
    appearance: '外観', notifications: '通知',
    integrationsLabel: '連携', security: 'セキュリティ', language: '言語',
    saveChanges: '変更を保存', saving: '保存中…',
    languageDesc: 'プラットフォームとチャット応答の言語',
    languageChanged: '言語を変更しました:',
    theme: 'テーマ', darkMode: 'ダークモード', lightMode: 'ライトモード',
    yourProfile: 'マイプロフィール', changePhoto: '写真を変更',
    search: '検索', cancel: 'キャンセル', close: '閉じる', delete: '削除',
    connect: '接続', update: '更新', back: '戻る', accept: '承認',
    comingSoon: '近日公開', loading: '読み込み中…',
    viewMore: 'もっと見る', viewLess: '折りたたむ', play: '再生', pause: '一時停止',
    download: 'ダウンロード', generate: '生成', clear: 'クリア', retry: '再試行',
    noData: 'データなし', errorOccurred: 'エラーが発生しました', save: '保存',
    searchBooks: 'タイトル、著者または言語で検索…',
    okbookSource: 'LibriVox · 20,000以上',
    continueListening: '続きを聞く',
    exploreGenres: 'ジャンルを探索',
    newArrivals: 'LibriVoxの新着',
    myFavorites: 'お気に入り',
    narrateWithAI: 'AIで読み上げ',
    narrateHint: 'テキスト、章、または本の一節を貼り付けてください…',
    generateNarration: 'テキストを読み上げ',
    generatingAudio: '音声を生成中…',
    noFavorites: 'まだお気に入りがありません',
    noFavoritesHint: '❤️をタップしてオーディオブックを保存',
    noResults: 'オーディオブックが見つかりません',
    noResultsHint: '別のキーワードやジャンルを試してください',
    chapters: '章', narrator: 'ナレーター',
    addToFavorites: 'お気に入りに追加', removeFromFavorites: 'お気に入り済み',
    playbackSpeed: '速度', rewind30: '30秒戻る', forward30: '30秒進む',
    nowPlaying: '再生中…', newSearch: '新しい検索…',
    searchingBooks: 'オーディオブックを検索中…', readyAudio: '音声準備完了',
    downloadMp3: 'MP3をダウンロード', charsMax: '文字',
    resultsFor: '検索結果:', noChapters: 'チャプターがありません',
    genre_adventure: '冒険', genre_classics: '古典', genre_mystery: 'ミステリー',
    genre_science: '科学', genre_history: '歴史', genre_fantasy: 'ファンタジー',
    genre_romance: 'ロマンス', genre_horror: 'ホラー', genre_philosophy: '哲学',
    genre_biography: '伝記', genre_children: '児童書', genre_poetry: '詩',
  },
  // ════════════════════════════════════════════════════════════════════════════
  zh: {
    chat: '聊天', intelligence: 'OkBot AI+', marketplace: '市场',
    calendar: '日历', notes: '笔记', reminders: '提醒',
    lists: '列表', finance: '财务', health: '健康', travel: '旅行',
    music: '音乐', documents: 'AI文档', memes: '表情包',
    movies: '电影与剧集', support: '支持', integrations: '集成',
    okhome: 'OkHome', okbook: 'OkBook', settings: '设置', logout: '退出',
    chatPlaceholder: '输入消息… (Enter发送，Shift+Enter换行)',
    listen: '听', stop: '停止', voice: '语音',
    newChat: '新对话', history: '历史', share: '分享',
    sendError: '发送错误', recording: '录音中… 再次点击发送',
    transcribing: '转录中…', transcribeError: '转录错误',
    audioTooShort: '音频太短', noAudio: '无法识别音频',
    moreDetail: '给我更详细的回答', thinking: '思考中…',
    settingsTitle: '设置', profile: '个人资料', plans: 'OkBot计划',
    appearance: '外观', notifications: '通知',
    integrationsLabel: '集成', security: '安全', language: '语言',
    saveChanges: '保存更改', saving: '保存中…',
    languageDesc: '平台语言和聊天响应语言',
    languageChanged: '语言已更改为',
    theme: '主题', darkMode: '深色模式', lightMode: '浅色模式',
    yourProfile: '我的资料', changePhoto: '更换照片',
    search: '搜索', cancel: '取消', close: '关闭', delete: '删除',
    connect: '连接', update: '更新', back: '返回', accept: '接受',
    comingSoon: '即将推出', loading: '加载中…',
    viewMore: '查看更多', viewLess: '收起', play: '播放', pause: '暂停',
    download: '下载', generate: '生成', clear: '清除', retry: '重试',
    noData: '无数据', errorOccurred: '发生错误', save: '保存',
    searchBooks: '按标题、作者或语言搜索…',
    okbookSource: 'LibriVox · 20,000+册',
    continueListening: '继续收听',
    exploreGenres: '探索类型',
    newArrivals: 'LibriVox最新',
    myFavorites: '我的收藏',
    narrateWithAI: 'AI朗读',
    narrateHint: '粘贴您的文本、章节或书摘…',
    generateNarration: '朗读文本',
    generatingAudio: '正在生成音频…',
    noFavorites: '还没有收藏',
    noFavoritesHint: '点击❤️保存有声书',
    noResults: '未找到有声书',
    noResultsHint: '尝试其他搜索词或类型',
    chapters: '章节', narrator: '朗读者',
    addToFavorites: '加入收藏', removeFromFavorites: '已收藏',
    playbackSpeed: '速度', rewind30: '后退30秒', forward30: '前进30秒',
    nowPlaying: '正在播放…', newSearch: '新搜索…',
    searchingBooks: '搜索有声书…', readyAudio: '音频就绪',
    downloadMp3: '下载MP3', charsMax: '字符',
    resultsFor: '搜索结果:', noChapters: '暂无章节',
    genre_adventure: '冒险', genre_classics: '经典', genre_mystery: '悬疑',
    genre_science: '科学', genre_history: '历史', genre_fantasy: '奇幻',
    genre_romance: '浪漫', genre_horror: '恐怖', genre_philosophy: '哲学',
    genre_biography: '传记', genre_children: '儿童', genre_poetry: '诗歌',
  },
  // ════════════════════════════════════════════════════════════════════════════
  ko: {
    chat: '채팅', intelligence: 'OkBot AI+', marketplace: '마켓플레이스',
    calendar: '캘린더', notes: '노트', reminders: '알림',
    lists: '목록', finance: '재무', health: '건강', travel: '여행',
    music: '음악', documents: 'AI 문서', memes: '밈',
    movies: '영화 & 시리즈', support: '지원', integrations: '연동',
    okhome: 'OkHome', okbook: 'OkBook', settings: '설정', logout: '로그아웃',
    chatPlaceholder: '메시지 입력… (Enter 전송, Shift+Enter 줄바꿈)',
    listen: '듣기', stop: '중지', voice: '음성',
    newChat: '새 대화', history: '기록', share: '공유',
    sendError: '전송 오류', recording: '녹음 중… 다시 탭하여 전송',
    transcribing: '변환 중…', transcribeError: '변환 오류',
    audioTooShort: '오디오가 너무 짧습니다', noAudio: '오디오를 인식할 수 없습니다',
    moreDetail: '더 자세한 답변 주세요', thinking: '생각 중…',
    settingsTitle: '설정', profile: '프로필', plans: 'OkBot 플랜',
    appearance: '모양', notifications: '알림',
    integrationsLabel: '연동', security: '보안', language: '언어',
    saveChanges: '변경 저장', saving: '저장 중…',
    languageDesc: '플랫폼과 채팅 응답 언어',
    languageChanged: '언어 변경:',
    theme: '테마', darkMode: '다크 모드', lightMode: '라이트 모드',
    yourProfile: '내 프로필', changePhoto: '사진 변경',
    search: '검색', cancel: '취소', close: '닫기', delete: '삭제',
    connect: '연결', update: '업데이트', back: '뒤로', accept: '수락',
    comingSoon: '출시 예정', loading: '로드 중…',
    viewMore: '더 보기', viewLess: '접기', play: '재생', pause: '일시정지',
    download: '다운로드', generate: '생성', clear: '지우기', retry: '재시도',
    noData: '데이터 없음', errorOccurred: '오류 발생', save: '저장',
    searchBooks: '제목, 저자 또는 언어로 검색…',
    okbookSource: 'LibriVox · 20,000+권',
    continueListening: '이어 듣기',
    exploreGenres: '장르 탐색',
    newArrivals: 'LibriVox 신작',
    myFavorites: '내 즐겨찾기',
    narrateWithAI: 'AI로 낭독',
    narrateHint: '텍스트, 챕터 또는 책 발췌문을 붙여넣으세요…',
    generateNarration: '텍스트 낭독',
    generatingAudio: '오디오 생성 중…',
    noFavorites: '즐겨찾기가 없습니다',
    noFavoritesHint: '❤️를 눌러 오디오북을 저장하세요',
    noResults: '오디오북을 찾을 수 없습니다',
    noResultsHint: '다른 검색어나 장르를 시도해보세요',
    chapters: '챕터', narrator: '낭독자',
    addToFavorites: '즐겨찾기 추가', removeFromFavorites: '즐겨찾기됨',
    playbackSpeed: '속도', rewind30: '30초 뒤로', forward30: '30초 앞으로',
    nowPlaying: '재생 중…', newSearch: '새 검색…',
    searchingBooks: '오디오북 검색 중…', readyAudio: '오디오 준비됨',
    downloadMp3: 'MP3 다운로드', charsMax: '자',
    resultsFor: '검색 결과:', noChapters: '챕터 없음',
    genre_adventure: '모험', genre_classics: '고전', genre_mystery: '미스터리',
    genre_science: '과학', genre_history: '역사', genre_fantasy: '판타지',
    genre_romance: '로맨스', genre_horror: '공포', genre_philosophy: '철학',
    genre_biography: '전기', genre_children: '아동', genre_poetry: '시',
  },
  // ════════════════════════════════════════════════════════════════════════════
  ar: {
    chat: 'دردشة', intelligence: 'OkBot AI+', marketplace: 'السوق',
    calendar: 'التقويم', notes: 'ملاحظات', reminders: 'التذكيرات',
    lists: 'القوائم', finance: 'المالية', health: 'الصحة', travel: 'السفر',
    music: 'الموسيقى', documents: 'مستندات الذكاء الاصطناعي', memes: 'ميمز',
    movies: 'أفلام ومسلسلات', support: 'الدعم', integrations: 'التكاملات',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'الإعدادات', logout: 'تسجيل خروج',
    chatPlaceholder: 'اكتب رسالتك… (Enter للإرسال، Shift+Enter سطر جديد)',
    listen: 'استماع', stop: 'إيقاف', voice: 'صوت',
    newChat: 'محادثة جديدة', history: 'السجل', share: 'مشاركة',
    sendError: 'خطأ في الإرسال', recording: 'جاري التسجيل… اضغط مرة أخرى للإرسال',
    transcribing: 'جاري التحويل…', transcribeError: 'خطأ في التحويل',
    audioTooShort: 'الصوت قصير جداً', noAudio: 'لم يتم فهم الصوت',
    moreDetail: 'أعطني إجابة أكثر تفصيلاً', thinking: 'أفكر…',
    settingsTitle: 'الإعدادات', profile: 'الملف الشخصي', plans: 'خطط OkBot',
    appearance: 'المظهر', notifications: 'الإشعارات',
    integrationsLabel: 'التكاملات', security: 'الأمان', language: 'اللغة',
    saveChanges: 'حفظ التغييرات', saving: 'جاري الحفظ…',
    languageDesc: 'لغة المنصة ولغة ردود الدردشة',
    languageChanged: 'تم تغيير اللغة إلى',
    theme: 'المظهر', darkMode: 'الوضع المظلم', lightMode: 'الوضع الفاتح',
    yourProfile: 'ملفي الشخصي', changePhoto: 'تغيير الصورة',
    search: 'بحث', cancel: 'إلغاء', close: 'إغلاق', delete: 'حذف',
    connect: 'اتصال', update: 'تحديث', back: 'رجوع', accept: 'قبول',
    comingSoon: 'قريباً', loading: 'جاري التحميل…',
    viewMore: 'عرض المزيد', viewLess: 'عرض أقل', play: 'تشغيل', pause: 'إيقاف مؤقت',
    download: 'تنزيل', generate: 'إنشاء', clear: 'مسح', retry: 'إعادة المحاولة',
    noData: 'لا توجد بيانات', errorOccurred: 'حدث خطأ', save: 'حفظ',
    searchBooks: 'البحث بالعنوان أو المؤلف أو اللغة…',
    okbookSource: 'LibriVox · +20,000 كتاب',
    continueListening: 'متابعة الاستماع',
    exploreGenres: 'استكشاف الأنواع',
    newArrivals: 'الجديد في LibriVox',
    myFavorites: 'مفضلتي',
    narrateWithAI: 'السرد بالذكاء الاصطناعي',
    narrateHint: 'الصق نصك أو فصلك أو مقتطف الكتاب…',
    generateNarration: 'سرد النص',
    generatingAudio: 'جاري إنشاء الصوت…',
    noFavorites: 'لا توجد مفضلات بعد',
    noFavoritesHint: 'اضغط ❤️ على أي كتاب مسموع لحفظه',
    noResults: 'لم يتم العثور على كتب مسموعة',
    noResultsHint: 'جرب مصطلحاً أو نوعاً مختلفاً',
    chapters: 'الفصول', narrator: 'الراوي',
    addToFavorites: 'إضافة للمفضلة', removeFromFavorites: 'في المفضلة',
    playbackSpeed: 'السرعة', rewind30: 'رجوع 30 ثانية', forward30: 'تقديم 30 ثانية',
    nowPlaying: 'يعزف الآن…', newSearch: 'بحث جديد…',
    searchingBooks: 'جاري البحث…', readyAudio: 'الصوت جاهز',
    downloadMp3: 'تنزيل MP3', charsMax: 'حرف',
    resultsFor: 'نتائج لـ', noChapters: 'لا توجد فصول',
    genre_adventure: 'مغامرة', genre_classics: 'كلاسيكيات', genre_mystery: 'غموض',
    genre_science: 'علوم', genre_history: 'تاريخ', genre_fantasy: 'خيال',
    genre_romance: 'رومانسية', genre_horror: 'رعب', genre_philosophy: 'فلسفة',
    genre_biography: 'سير ذاتية', genre_children: 'أطفال', genre_poetry: 'شعر',
  },
  // ════════════════════════════════════════════════════════════════════════════
  ru: {
    chat: 'Чат', intelligence: 'OkBot AI+', marketplace: 'Маркетплейс',
    calendar: 'Календарь', notes: 'Заметки', reminders: 'Напоминания',
    lists: 'Списки', finance: 'Финансы', health: 'Здоровье', travel: 'Путешествия',
    music: 'Музыка', documents: 'ИИ-документы', memes: 'Мемы',
    movies: 'Фильмы & Сериалы', support: 'Поддержка', integrations: 'Интеграции',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Настройки', logout: 'Выйти',
    chatPlaceholder: 'Введите сообщение… (Enter отправить, Shift+Enter новая строка)',
    listen: 'Слушать', stop: 'Стоп', voice: 'Голос',
    newChat: 'Новый разговор', history: 'История', share: 'Поделиться',
    sendError: 'Ошибка отправки', recording: 'Запись… нажмите снова для отправки',
    transcribing: 'Транскрипция…', transcribeError: 'Ошибка транскрипции',
    audioTooShort: 'Аудио слишком короткое', noAudio: 'Аудио не распознано',
    moreDetail: 'Дайте более подробный ответ', thinking: 'Думаю…',
    settingsTitle: 'Настройки', profile: 'Профиль', plans: 'Планы OkBot',
    appearance: 'Внешний вид', notifications: 'Уведомления',
    integrationsLabel: 'Интеграции', security: 'Безопасность', language: 'Язык',
    saveChanges: 'Сохранить', saving: 'Сохранение…',
    languageDesc: 'Язык платформы и ответов в чате',
    languageChanged: 'Язык изменён на',
    theme: 'Тема', darkMode: 'Тёмная тема', lightMode: 'Светлая тема',
    yourProfile: 'Мой профиль', changePhoto: 'Изменить фото',
    search: 'Поиск', cancel: 'Отмена', close: 'Закрыть', delete: 'Удалить',
    connect: 'Подключить', update: 'Обновить', back: 'Назад', accept: 'Принять',
    comingSoon: 'Скоро', loading: 'Загрузка…',
    viewMore: 'Показать больше', viewLess: 'Свернуть', play: 'Воспроизвести', pause: 'Пауза',
    download: 'Скачать', generate: 'Создать', clear: 'Очистить', retry: 'Повторить',
    noData: 'Нет данных', errorOccurred: 'Произошла ошибка', save: 'Сохранить',
    searchBooks: 'Поиск по названию, автору или языку…',
    okbookSource: 'LibriVox · 20 000+ книг',
    continueListening: 'Продолжить слушать',
    exploreGenres: 'Исследовать жанры',
    newArrivals: 'Новинки LibriVox',
    myFavorites: 'Мои избранные',
    narrateWithAI: 'Озвучить с ИИ',
    narrateHint: 'Вставьте текст, главу или отрывок книги…',
    generateNarration: 'Озвучить текст',
    generatingAudio: 'Генерация аудио…',
    noFavorites: 'Нет избранных',
    noFavoritesHint: 'Нажмите ❤️ на аудиокниге, чтобы сохранить',
    noResults: 'Аудиокниги не найдены',
    noResultsHint: 'Попробуйте другой запрос или жанр',
    chapters: 'Главы', narrator: 'Рассказчик',
    addToFavorites: 'Добавить в избранное', removeFromFavorites: 'В избранном',
    playbackSpeed: 'Скорость', rewind30: 'Назад 30с', forward30: 'Вперёд 30с',
    nowPlaying: 'Воспроизведение…', newSearch: 'Новый поиск…',
    searchingBooks: 'Поиск аудиокниг…', readyAudio: 'Аудио готово',
    downloadMp3: 'Скачать MP3', charsMax: 'символов',
    resultsFor: 'Результаты для', noChapters: 'Нет доступных глав',
    genre_adventure: 'Приключения', genre_classics: 'Классика', genre_mystery: 'Детектив',
    genre_science: 'Наука', genre_history: 'История', genre_fantasy: 'Фэнтези',
    genre_romance: 'Романтика', genre_horror: 'Ужасы', genre_philosophy: 'Философия',
    genre_biography: 'Биографии', genre_children: 'Детские', genre_poetry: 'Поэзия',
  },
  // ════════════════════════════════════════════════════════════════════════════
  nl: {
    chat: 'Chat', intelligence: 'OkBot AI+', marketplace: 'Marktplaats',
    calendar: 'Kalender', notes: 'Notities', reminders: 'Herinneringen',
    lists: 'Lijsten', finance: 'Financiën', health: 'Gezondheid', travel: 'Reizen',
    music: 'Muziek', documents: 'AI-documenten', memes: 'Memes',
    movies: 'Films & Series', support: 'Ondersteuning', integrations: 'Integraties',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Instellingen', logout: 'Uitloggen',
    chatPlaceholder: 'Typ uw bericht… (Enter verzenden, Shift+Enter nieuwe regel)',
    listen: 'Beluisteren', stop: 'Stoppen', voice: 'Stem',
    newChat: 'Nieuw gesprek', history: 'Geschiedenis', share: 'Delen',
    sendError: 'Fout bij verzenden', recording: 'Opname… tik nogmaals om te verzenden',
    transcribing: 'Transcriptie…', transcribeError: 'Transcriptiefout',
    audioTooShort: 'Audio te kort', noAudio: 'Audio niet begrepen',
    moreDetail: 'Geef me een uitgebreider antwoord', thinking: 'Nadenken…',
    settingsTitle: 'Instellingen', profile: 'Profiel', plans: 'OkBot-plannen',
    appearance: 'Weergave', notifications: 'Meldingen',
    integrationsLabel: 'Integraties', security: 'Beveiliging', language: 'Taal',
    saveChanges: 'Wijzigingen opslaan', saving: 'Opslaan…',
    languageDesc: 'Platformtaal en chatantwoordtaal',
    languageChanged: 'Taal gewijzigd naar',
    theme: 'Thema', darkMode: 'Donkere modus', lightMode: 'Lichte modus',
    yourProfile: 'Mijn Profiel', changePhoto: 'Foto wijzigen',
    search: 'Zoeken', cancel: 'Annuleren', close: 'Sluiten', delete: 'Verwijderen',
    connect: 'Verbinden', update: 'Bijwerken', back: 'Terug', accept: 'Accepteren',
    comingSoon: 'Binnenkort', loading: 'Laden…',
    viewMore: 'Meer zien', viewLess: 'Minder', play: 'Afspelen', pause: 'Pauzeren',
    download: 'Downloaden', generate: 'Genereren', clear: 'Wissen', retry: 'Opnieuw',
    noData: 'Geen gegevens', errorOccurred: 'Er is een fout opgetreden', save: 'Opslaan',
    searchBooks: 'Zoek op titel, auteur of taal…',
    okbookSource: 'LibriVox · 20k+ titels',
    continueListening: 'Doorgaan met luisteren',
    exploreGenres: 'Genres verkennen',
    newArrivals: 'Nieuw bij LibriVox',
    myFavorites: 'Mijn favorieten',
    narrateWithAI: 'AI-vertelling',
    narrateHint: 'Plak uw tekst, hoofdstuk of boekfragment…',
    generateNarration: 'Tekst voorlezen',
    generatingAudio: 'Audio genereren…',
    noFavorites: 'Nog geen favorieten',
    noFavoritesHint: 'Tik op ❤️ om een audioboek op te slaan',
    noResults: 'Geen audioboeken gevonden',
    noResultsHint: 'Probeer een andere term of genre',
    chapters: 'Hoofdstukken', narrator: 'Verteller',
    addToFavorites: 'Toevoegen aan favorieten', removeFromFavorites: 'In favorieten',
    playbackSpeed: 'Snelheid', rewind30: '30s terugspoelen', forward30: '30s vooruit',
    nowPlaying: 'Nu afspelen…', newSearch: 'Nieuwe zoekopdracht…',
    searchingBooks: 'Audioboeken zoeken…', readyAudio: 'Audio klaar',
    downloadMp3: 'MP3 downloaden', charsMax: 'tekens',
    resultsFor: 'Resultaten voor', noChapters: 'Geen hoofdstukken beschikbaar',
    genre_adventure: 'Avontuur', genre_classics: 'Klassiekers', genre_mystery: 'Mysterie',
    genre_science: 'Wetenschap', genre_history: 'Geschiedenis', genre_fantasy: 'Fantasy',
    genre_romance: 'Romantiek', genre_horror: 'Horror', genre_philosophy: 'Filosofie',
    genre_biography: 'Biografieën', genre_children: 'Kinderen', genre_poetry: 'Poëzie',
  },
  // ════════════════════════════════════════════════════════════════════════════
  tr: {
    chat: 'Sohbet', intelligence: 'OkBot AI+', marketplace: 'Pazar',
    calendar: 'Takvim', notes: 'Notlar', reminders: 'Hatırlatıcılar',
    lists: 'Listeler', finance: 'Finans', health: 'Sağlık', travel: 'Seyahat',
    music: 'Müzik', documents: 'AI Belgeler', memes: 'Meme',
    movies: 'Film & Dizi', support: 'Destek', integrations: 'Entegrasyonlar',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Ayarlar', logout: 'Çıkış',
    chatPlaceholder: 'Mesajınızı yazın… (Enter göndermek, Shift+Enter yeni satır)',
    listen: 'Dinle', stop: 'Durdur', voice: 'Ses',
    newChat: 'Yeni konuşma', history: 'Geçmiş', share: 'Paylaş',
    sendError: 'Gönderme hatası', recording: 'Kaydediliyor… tekrar dokun',
    transcribing: 'Ses dönüştürülüyor…', transcribeError: 'Dönüştürme hatası',
    audioTooShort: 'Ses çok kısa', noAudio: 'Ses anlaşılamadı',
    moreDetail: 'Daha ayrıntılı bir cevap ver', thinking: 'Düşünüyor…',
    settingsTitle: 'Ayarlar', profile: 'Profil', plans: 'OkBot Planları',
    appearance: 'Görünüm', notifications: 'Bildirimler',
    integrationsLabel: 'Entegrasyonlar', security: 'Güvenlik', language: 'Dil',
    saveChanges: 'Değişiklikleri kaydet', saving: 'Kaydediliyor…',
    languageDesc: 'Platform dili ve sohbet yanıt dili',
    languageChanged: 'Dil değiştirildi:',
    theme: 'Tema', darkMode: 'Karanlık mod', lightMode: 'Aydınlık mod',
    yourProfile: 'Profilim', changePhoto: 'Fotoğrafı değiştir',
    search: 'Ara', cancel: 'İptal', close: 'Kapat', delete: 'Sil',
    connect: 'Bağlan', update: 'Güncelle', back: 'Geri', accept: 'Kabul et',
    comingSoon: 'Yakında', loading: 'Yükleniyor…',
    viewMore: 'Daha fazla', viewLess: 'Daha az', play: 'Oynat', pause: 'Duraklat',
    download: 'İndir', generate: 'Oluştur', clear: 'Temizle', retry: 'Tekrar dene',
    noData: 'Veri yok', errorOccurred: 'Bir hata oluştu', save: 'Kaydet',
    searchBooks: 'Başlık, yazar veya dile göre ara…',
    okbookSource: 'LibriVox · 20 bin+ başlık',
    continueListening: 'Dinlemeye devam et',
    exploreGenres: 'Türleri keşfet',
    newArrivals: 'LibriVox\'ta yeni',
    myFavorites: 'Favorilerim',
    narrateWithAI: 'AI ile anlat',
    narrateHint: 'Metninizi, bölümünüzü veya alıntınızı yapıştırın…',
    generateNarration: 'Metni seslendirler',
    generatingAudio: 'Ses oluşturuluyor…',
    noFavorites: 'Henüz favori yok',
    noFavoritesHint: '❤️ ile sesli kitabı kaydedin',
    noResults: 'Sesli kitap bulunamadı',
    noResultsHint: 'Başka bir terim veya tür deneyin',
    chapters: 'Bölümler', narrator: 'Anlatıcı',
    addToFavorites: 'Favorilere ekle', removeFromFavorites: 'Favorilerde',
    playbackSpeed: 'Hız', rewind30: '30s geri', forward30: '30s ileri',
    nowPlaying: 'Oynatılıyor…', newSearch: 'Yeni arama…',
    searchingBooks: 'Sesli kitap aranıyor…', readyAudio: 'Ses hazır',
    downloadMp3: 'MP3 indir', charsMax: 'karakter',
    resultsFor: 'Sonuçlar:', noChapters: 'Bölüm mevcut değil',
    genre_adventure: 'Macera', genre_classics: 'Klasikler', genre_mystery: 'Gizem',
    genre_science: 'Bilim', genre_history: 'Tarih', genre_fantasy: 'Fantezi',
    genre_romance: 'Romantik', genre_horror: 'Korku', genre_philosophy: 'Felsefe',
    genre_biography: 'Biyografi', genre_children: 'Çocuk', genre_poetry: 'Şiir',
  },
  // ════════════════════════════════════════════════════════════════════════════
  pl: {
    chat: 'Czat', intelligence: 'OkBot AI+', marketplace: 'Rynek',
    calendar: 'Kalendarz', notes: 'Notatki', reminders: 'Przypomnienia',
    lists: 'Listy', finance: 'Finanse', health: 'Zdrowie', travel: 'Podróże',
    music: 'Muzyka', documents: 'Dokumenty AI', memes: 'Memy',
    movies: 'Filmy & Seriale', support: 'Wsparcie', integrations: 'Integracje',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Ustawienia', logout: 'Wyloguj',
    chatPlaceholder: 'Wpisz wiadomość… (Enter wysyła, Shift+Enter nowa linia)',
    listen: 'Słuchaj', stop: 'Stop', voice: 'Głos',
    newChat: 'Nowa rozmowa', history: 'Historia', share: 'Udostępnij',
    sendError: 'Błąd wysyłania', recording: 'Nagrywanie… dotknij ponownie',
    transcribing: 'Transkrypcja…', transcribeError: 'Błąd transkrypcji',
    audioTooShort: 'Audio zbyt krótkie', noAudio: 'Nie rozpoznano dźwięku',
    moreDetail: 'Daj bardziej szczegółową odpowiedź', thinking: 'Myślę…',
    settingsTitle: 'Ustawienia', profile: 'Profil', plans: 'Plany OkBot',
    appearance: 'Wygląd', notifications: 'Powiadomienia',
    integrationsLabel: 'Integracje', security: 'Bezpieczeństwo', language: 'Język',
    saveChanges: 'Zapisz zmiany', saving: 'Zapisywanie…',
    languageDesc: 'Język platformy i odpowiedzi czatu',
    languageChanged: 'Język zmieniony na',
    theme: 'Motyw', darkMode: 'Tryb ciemny', lightMode: 'Tryb jasny',
    yourProfile: 'Mój Profil', changePhoto: 'Zmień zdjęcie',
    search: 'Szukaj', cancel: 'Anuluj', close: 'Zamknij', delete: 'Usuń',
    connect: 'Połącz', update: 'Aktualizuj', back: 'Wstecz', accept: 'Akceptuj',
    comingSoon: 'Wkrótce', loading: 'Ładowanie…',
    viewMore: 'Zobacz więcej', viewLess: 'Zwiń', play: 'Odtwórz', pause: 'Wstrzymaj',
    download: 'Pobierz', generate: 'Generuj', clear: 'Wyczyść', retry: 'Ponów',
    noData: 'Brak danych', errorOccurred: 'Wystąpił błąd', save: 'Zapisz',
    searchBooks: 'Szukaj po tytule, autorze lub języku…',
    okbookSource: 'LibriVox · 20 000+ tytułów',
    continueListening: 'Kontynuuj słuchanie',
    exploreGenres: 'Przeglądaj gatunki',
    newArrivals: 'Nowości LibriVox',
    myFavorites: 'Moje ulubione',
    narrateWithAI: 'Czytaj z AI',
    narrateHint: 'Wklej tekst, rozdział lub fragment książki…',
    generateNarration: 'Czytaj tekst',
    generatingAudio: 'Generowanie audio…',
    noFavorites: 'Brak ulubionych',
    noFavoritesHint: 'Naciśnij ❤️ na audiobookach, aby je zapisać',
    noResults: 'Nie znaleziono audiobooków',
    noResultsHint: 'Spróbuj innego terminu lub gatunku',
    chapters: 'Rozdziały', narrator: 'Narrator',
    addToFavorites: 'Dodaj do ulubionych', removeFromFavorites: 'W ulubionych',
    playbackSpeed: 'Prędkość', rewind30: '30s wstecz', forward30: '30s naprzód',
    nowPlaying: 'Odtwarzanie…', newSearch: 'Nowe wyszukiwanie…',
    searchingBooks: 'Szukam audiobooków…', readyAudio: 'Audio gotowe',
    downloadMp3: 'Pobierz MP3', charsMax: 'znaków',
    resultsFor: 'Wyniki dla', noChapters: 'Brak dostępnych rozdziałów',
    genre_adventure: 'Przygoda', genre_classics: 'Klasyka', genre_mystery: 'Kryminał',
    genre_science: 'Nauka', genre_history: 'Historia', genre_fantasy: 'Fantasy',
    genre_romance: 'Romans', genre_horror: 'Horror', genre_philosophy: 'Filozofia',
    genre_biography: 'Biografie', genre_children: 'Dla dzieci', genre_poetry: 'Poezja',
  },
  // ════════════════════════════════════════════════════════════════════════════
  id: {
    chat: 'Obrolan', intelligence: 'OkBot AI+', marketplace: 'Pasar',
    calendar: 'Kalender', notes: 'Catatan', reminders: 'Pengingat',
    lists: 'Daftar', finance: 'Keuangan', health: 'Kesehatan', travel: 'Perjalanan',
    music: 'Musik', documents: 'Dokumen AI', memes: 'Meme',
    movies: 'Film & Serial', support: 'Dukungan', integrations: 'Integrasi',
    okhome: 'OkHome', okbook: 'OkBook', settings: 'Pengaturan', logout: 'Keluar',
    chatPlaceholder: 'Ketik pesan… (Enter kirim, Shift+Enter baris baru)',
    listen: 'Dengar', stop: 'Berhenti', voice: 'Suara',
    newChat: 'Percakapan baru', history: 'Riwayat', share: 'Bagikan',
    sendError: 'Gagal mengirim', recording: 'Merekam… ketuk lagi untuk mengirim',
    transcribing: 'Transkripsi…', transcribeError: 'Kesalahan transkripsi',
    audioTooShort: 'Audio terlalu pendek', noAudio: 'Audio tidak dipahami',
    moreDetail: 'Berikan jawaban yang lebih rinci', thinking: 'Berpikir…',
    settingsTitle: 'Pengaturan', profile: 'Profil', plans: 'Paket OkBot',
    appearance: 'Tampilan', notifications: 'Notifikasi',
    integrationsLabel: 'Integrasi', security: 'Keamanan', language: 'Bahasa',
    saveChanges: 'Simpan perubahan', saving: 'Menyimpan…',
    languageDesc: 'Bahasa platform dan respons obrolan',
    languageChanged: 'Bahasa diubah ke',
    theme: 'Tema', darkMode: 'Mode gelap', lightMode: 'Mode terang',
    yourProfile: 'Profil Saya', changePhoto: 'Ganti foto',
    search: 'Cari', cancel: 'Batal', close: 'Tutup', delete: 'Hapus',
    connect: 'Hubungkan', update: 'Perbarui', back: 'Kembali', accept: 'Terima',
    comingSoon: 'Segera hadir', loading: 'Memuat…',
    viewMore: 'Lihat lebih', viewLess: 'Lebih sedikit', play: 'Putar', pause: 'Jeda',
    download: 'Unduh', generate: 'Buat', clear: 'Hapus', retry: 'Coba lagi',
    noData: 'Tidak ada data', errorOccurred: 'Terjadi kesalahan', save: 'Simpan',
    searchBooks: 'Cari berdasarkan judul, penulis, atau bahasa…',
    okbookSource: 'LibriVox · 20.000+ judul',
    continueListening: 'Lanjutkan mendengarkan',
    exploreGenres: 'Jelajahi genre',
    newArrivals: 'Terbaru di LibriVox',
    myFavorites: 'Favorit saya',
    narrateWithAI: 'Narasi dengan AI',
    narrateHint: 'Tempel teks, bab, atau kutipan buku Anda…',
    generateNarration: 'Narasi teks',
    generatingAudio: 'Menghasilkan audio…',
    noFavorites: 'Belum ada favorit',
    noFavoritesHint: 'Ketuk ❤️ pada buku audio untuk menyimpannya',
    noResults: 'Tidak ada buku audio ditemukan',
    noResultsHint: 'Coba istilah atau genre lain',
    chapters: 'Bab', narrator: 'Narator',
    addToFavorites: 'Tambah ke favorit', removeFromFavorites: 'Di favorit',
    playbackSpeed: 'Kecepatan', rewind30: 'Mundur 30d', forward30: 'Maju 30d',
    nowPlaying: 'Sedang diputar…', newSearch: 'Pencarian baru…',
    searchingBooks: 'Mencari buku audio…', readyAudio: 'Audio siap',
    downloadMp3: 'Unduh MP3', charsMax: 'karakter',
    resultsFor: 'Hasil untuk', noChapters: 'Tidak ada bab tersedia',
    genre_adventure: 'Petualangan', genre_classics: 'Klasik', genre_mystery: 'Misteri',
    genre_science: 'Sains', genre_history: 'Sejarah', genre_fantasy: 'Fantasi',
    genre_romance: 'Romantis', genre_horror: 'Horor', genre_philosophy: 'Filsafat',
    genre_biography: 'Biografi', genre_children: 'Anak-anak', genre_poetry: 'Puisi',
  },
};

// ── Context ───────────────────────────────────────────────────────────────────
const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      const stored = localStorage.getItem('okbot_ui_lang');
      // Validate stored code exists in T
      return (stored && T[stored]) ? stored : 'es';
    } catch { return 'es'; }
  });

  const setLanguage = (code) => {
    if (!T[code]) return; // Reject invalid codes
    setLanguageState(code);
    try { localStorage.setItem('okbot_ui_lang', code); } catch {}
    try { localStorage.setItem('okbot_tts_lang', code); } catch {}
    // Update document lang for accessibility
    try { document.documentElement.lang = code; } catch {}
  };

  const t = (key) => {
    const translations = T[language] || T['es'];
    return translations[key] ?? T['es'][key] ?? key;
  };

  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  // Sync document lang on mount and change
  useEffect(() => {
    try { document.documentElement.lang = language; } catch {}
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, currentLang, LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside <LanguageProvider>');
  return ctx;
}

export default LanguageContext;
